import { zodResolver } from "@hookform/resolvers/zod";
import { newIdempotencyKey } from "@station/api-client";
import { Alert, Button, Card, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { UserPlus } from "lucide-react";
import { type ReactElement, type ReactNode, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  createCustomerBody,
  type CustomerFormValues,
  customerFormSchema,
  emptyCustomerForm,
  type InitialConsentInput,
  initialConsentRequests,
  initialPermissionRequests,
} from "../../../features/customers/customer-values.js";
import { InitialConsentsFields } from "../../../features/customers/initial-consents.js";
import { bindCustomerError, CustomerProfileFields } from "../../../features/customers/profile-form.js";
import {
  useCustomerPermissions,
  useCustomerStations,
  usePublishedTexts,
  useRefreshCustomers,
} from "../../../features/customers/queries.js";
import { CustomersPage, useCustomersCrumb } from "../../../features/customers/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";

export const Route = createFileRoute("/_app/musteriler/yeni")({ component: NewCustomerRoute });

function NewCustomerRoute(): ReactElement {
  const { t } = useTranslation();
  const crumb = useCustomersCrumb();
  const permissions = useCustomerPermissions();
  return (
    <CustomersPage
      title={t("customers.create.title")}
      description={t("customers.create.description")}
      breadcrumb={[crumb, { label: t("customers.create.title") }]}
    >
      {permissions.canManage ? (
        <NewCustomerForm />
      ) : (
        <Alert tone="info">{t("customers.create.noPermission")}</Alert>
      )}
    </CustomersPage>
  );
}

/**
 * Yeni müşteri (Prompt 17 web). Akış: `POST /customers` (Idempotency-Key) → işaretlenen rıza kayıtları → ileti
 * izinleri → ayrıntı. Rıza adımı hata verirse müşteri AÇIK kalır: yine ayrıntıya gidilir ve hata KALICI bir bildirimle
 * söylenir (rıza sekmesi açılır) — hiçbir karar sessizce kaybolmaz.
 */
function NewCustomerForm(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshCustomers();
  const permissions = useCustomerPermissions();
  const stations = useCustomerStations();
  // ADR 0017 §40: istasyon müdürü (`customers.manage`, KVKK izni yok) de aydınlatma / rıza / ileti iznini AYNI akışta
  // kaydeder — metinleri müşteri izniyle (yalnızca yayımdaki) okur.
  const withConsents = permissions.canRecordConsents;
  const texts = usePublishedTexts(withConsents && permissions.canReadPublishedTexts);
  const [formError, setFormError] = useState<string | null>(null);
  const [consents, setConsents] = useState<InitialConsentInput>({
    channel: "STATION",
    noticeAcknowledged: false,
    grantedConsentTextIds: [],
    messageChannels: [],
  });
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema()) as never,
    defaultValues: emptyCustomerForm(),
  });

  const save = useMutation({
    mutationFn: async (values: CustomerFormValues) => {
      const created = (
        await api.POST("/customers", {
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          body: createCustomerBody(values),
        })
      ).data!;
      // Müşteri AÇILDI. Bundan sonraki adımların hatası müşteriyi geri almaz; toplanıp kullanıcıya söylenir.
      const failures: string[] = [];
      const publishedTexts = withConsents ? (texts.data ?? []) : [];
      for (const body of initialConsentRequests(publishedTexts, consents)) {
        try {
          await api.POST("/privacy/customers/{id}/consents", { params: { path: { id: created.id } }, body });
        } catch (caught) {
          failures.push(errorMessage(caught));
        }
      }
      for (const body of initialPermissionRequests(publishedTexts, consents)) {
        try {
          await api.POST("/privacy/customers/{id}/message-permissions", {
            params: { path: { id: created.id } },
            body,
          });
        } catch (caught) {
          failures.push(errorMessage(caught));
        }
      }
      return { created, failures };
    },
    onSuccess: async ({ created, failures }) => {
      await refresh();
      if (failures.length === 0) {
        toast({ tone: "success", title: t("customers.create.created", { code: created.code }) });
      } else {
        toast({
          tone: "danger",
          title: t("customers.create.consentFailed", { code: created.code, count: failures.length }),
          description: [...new Set(failures)].join(" · "),
          duration: 0,
        });
      }
      await navigate({
        to: "/musteriler/$id",
        params: { id: created.id },
        search: failures.length === 0 ? {} : { tab: "consents" },
      });
    },
    onError: (caught) => setFormError(bindCustomerError(caught, form)),
  });

  const submit = form.handleSubmit((values) => {
    setFormError(null);
    save.mutate(values);
  });

  return (
    <form className="grid gap-4" noValidate onSubmit={(event) => void submit(event)}>
      {formError ? <Alert tone="danger">{formError}</Alert> : null}
      <FormSection title={t("customers.create.profile")}>
        <CustomerProfileFields form={form} stations={stations.data ?? []} showCardOption />
      </FormSection>
      {withConsents ? (
        <FormSection
          title={t("customers.create.consents.title")}
          description={t("customers.create.consents.description")}
        >
          <InitialConsentsFields
            texts={texts.data ?? []}
            loading={texts.isPending}
            error={texts.error}
            canReadTexts={permissions.canReadPublishedTexts}
            canOpenTextsPage={permissions.canReadPrivacy}
            value={consents}
            onChange={setConsents}
          />
        </FormSection>
      ) : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" asChild>
          <Link to="/musteriler">{t("common.cancel")}</Link>
        </Button>
        <Button type="submit" loading={save.isPending}>
          <UserPlus aria-hidden="true" />
          {t("customers.create.submit")}
        </Button>
      </div>
    </form>
  );
}

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): ReactElement {
  const headingId = useId();
  return (
    <Card className="grid gap-4 p-4 sm:p-5" aria-labelledby={headingId}>
      <div className="grid gap-1">
        <h2 id={headingId} className="text-base font-semibold">
          {title}
        </h2>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {children}
    </Card>
  );
}
